import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Edit, Download, History, Receipt, Plus, Lock } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { RateCardSheet } from '@/components/RateCard';
import { useBudgetCalculations } from '@/hooks/useBudgetCalculations';
import { TableCell, TableRow } from '@/components/ui/table';
import { DataTableEnhanced, type ColumnDef, type ColumnSort } from '@/components/ui/data-table-enhanced';
import { PaginationControls } from '@/components/pagination-controls';
import { calculatePagination, paginateArray, type PaginationMetadata } from '@/types/pagination';
import { formatCurrency, formatPercent, cn } from '@/lib/utils';
import { useColumnPreferences } from '@/hooks/use-column-preferences';
import { 
  COST_BUDGET_COLUMNS, 
  COST_BUDGET_STORAGE_KEY 
} from '@/lib/cost-budget-columns';
import { exportCostBudgetToExcel } from '@/lib/cost-budget-export-utils';
import type { CostCategory } from '@/types/budget.types';

interface CostBudgetTableProps {
  projectId: string;
  categories: CostCategory[];
  totals?: {
    budget: number;
    actual: number;
    committed: number;
    available: number;
    progressPercent: number;
  };
  resourceBudget?: any;
  onEdit?: () => void;
  onExport?: () => void;
  onHistory?: () => void;
}

export function CostBudgetTable({ 
  projectId,
  categories = [],
  totals,
  resourceBudget,
  onEdit,
  onExport,
  onHistory 
}: CostBudgetTableProps) {
  const [isRateCardOpen, setIsRateCardOpen] = useState(false);

  // Lift column preferences to parent so ColumnConfigButton and CostBudgetTableView share state
  const { columns, isLoading: columnsLoading } = useColumnPreferences({
    storageKey: COST_BUDGET_STORAGE_KEY,
    defaultColumns: COST_BUDGET_COLUMNS,
  });

  const { totalLabourCost } = useBudgetCalculations(
    projectId,
    resourceBudget || null
  );

  // Calculate totals from categories if not provided
  const totalBudget = totals?.budget ?? categories.reduce((sum, cat) => sum + (cat.budget || 0), 0);
  const totalActual = totals?.actual ?? categories.reduce((sum, cat) => sum + (cat.actual || 0), 0);
  const totalCommitted = totals?.committed ?? categories.reduce((sum, cat) => sum + (cat.committed || 0), 0);
  const totalRemaining = totalBudget - totalActual - totalCommitted;
  // Handle division by zero - if budget is 0, show 0% instead of NaN
  const totalPercentUsed = totalBudget > 0 ? ((totalActual + totalCommitted) / totalBudget) * 100 : 0;

  // Helper function to get status badge based on % used
  // Aligned with Resource Budget status thresholds:
  // - On Track: 0-79% used (Green)
  // - At Risk: 80-99% used (Amber/Yellow)
  // - Over Budget: 100%+ used (Red)
  // Special case: If budget is 0 and no actual/committed costs, show "No Budget"
  const getStatusBadge = (percentUsed: number, budget: number, actual: number, committed: number | null) => {
    // If budget is 0 and there are no costs, show "No Budget" (nothing to track)
    if (budget === 0 && actual === 0 && (committed === 0 || committed === null)) {
      return (
        <Badge variant="outline" className="text-muted-foreground">
          No Budget
        </Badge>
      );
    }
    
    // If budget is 0 but there are costs, show "Over Budget"
    if (budget === 0 && (actual > 0 || (committed && committed > 0))) {
      return (
        <Badge variant="destructive" accent="destructive">
          Over Budget
        </Badge>
      );
    }
    
    if (percentUsed < 80) {
      return (
        <Badge variant="success" accent="success">
          On Track
        </Badge>
      );
    } else if (percentUsed < 100) {
      return (
        <Badge variant="warning" accent="warning">
          At Risk
        </Badge>
      );
    } else {
      return (
        <Badge variant="destructive" accent="destructive">
          Over Budget
        </Badge>
      );
    }
  };

  // Helper function to calculate remaining and format with color
  const calculateRemaining = (budget: number, actual: number, committed: number | null) => {
    const remaining = budget - actual - (committed || 0);
    return {
      value: remaining,
      className: remaining >= 0 ? 'text-success-600' : 'text-error-600',
    };
  };

  // Handle export
  const handleExport = async () => {
    if (onExport) {
      onExport();
    } else {
      // Default export behavior
      const exportData = categories.map(cat => {
        const budget = cat.budget || 0;
        const actual = cat.actual || 0;
        const committed = cat.committed || 0;
        // Handle division by zero
        const percentUsed = budget > 0 ? ((actual + committed) / budget) * 100 : 0;
        const remaining = budget - actual - committed;
        let status = 'On Track';
        if (budget === 0 && actual === 0 && committed === 0) {
          status = 'No Budget';
        } else if (budget === 0 || percentUsed >= 100) {
          status = 'Over Budget';
        } else if (percentUsed >= 80) {
          status = 'At Risk';
        }

        return {
          category: cat.name,
          budget,
          actual,
          committed,
          remaining,
          percentUsed,
          status,
        };
      });

      await exportCostBudgetToExcel(exportData);
    }
  };

  // Show empty state if no categories
  if (categories.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <Card className="w-full max-w-md border-dashed">
          <CardContent className="flex flex-col items-center justify-center text-center p-12">
            <div className="rounded-full bg-muted p-4 mb-4">
              <Receipt className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No Cost Budget</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm">
              Track expenses, commitments, and cost categories by creating a cost budget for this project.
            </p>
            {onEdit && (
              <Button onClick={onEdit}>
                <Plus className="mr-2 h-4 w-4" />
                Create Cost Budget
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle>Cost Budget</CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setIsRateCardOpen(true)}>
              <Receipt className="mr-2 h-4 w-4" />
              Rates
            </Button>
            {onHistory && (
              <Button variant="outline" size="sm" onClick={onHistory}>
                <History className="mr-2 h-4 w-4" />
                Revision History
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
            {onEdit && (
              <Button size="sm" onClick={onEdit}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Button>
            )}

          </div>
        </CardHeader>
        <CardContent className="p-0 space-y-6">
          {/* One-line Summary */}
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg border border-border mx-6 mt-6">
            <div className="flex items-center gap-8">
              <div>
                <span className="text-sm text-muted-foreground">Budget:</span>
                <span className="ml-2 text-base font-semibold text-foreground">
                  {formatCurrency(totalBudget)}
                </span>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Actual:</span>
                <span className="ml-2 text-base font-semibold text-foreground">
                  {formatCurrency(totalActual)}
                </span>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Remaining:</span>
                <span className={cn(
                  "ml-2 text-base font-semibold",
                  totalRemaining < 0
                    ? "text-error-600"
                    : totalPercentUsed >= 80
                      ? "text-warning-600"
                      : totalRemaining > 0
                        ? "text-success-600"
                        : "text-neutral-900"
                )}>
                  {totalRemaining > 0 ? '+' : ''}{formatCurrency(totalRemaining)}
                </span>
              </div>
            </div>
          </div>
          <CostBudgetTableView 
            categories={categories}
            resourceBudget={resourceBudget}
            totalBudget={totalBudget}
            totalActual={totalActual}
            totalCommitted={totalCommitted}
            totalRemaining={totalRemaining}
            totalPercentUsed={totalPercentUsed}
            totalLabourCost={totalLabourCost}
            getStatusBadge={getStatusBadge}
            calculateRemaining={calculateRemaining}
            columns={columns}
            columnsLoading={columnsLoading}
          />
        </CardContent>
      </Card>

      {/* Rate Card Sheet */}
      <RateCardSheet
        open={isRateCardOpen}
        onOpenChange={setIsRateCardOpen}
        projectId={projectId}
      />
    </>
  );
}


// Row data type for the cost budget table — includes sentinel flag for the synthetic Labour group row
type CostBudgetRowData = {
  id: string;
  category: string;
  categoryCode?: string;
  budget: string;
  actual: string;
  committed: string;
  remaining: { text: string; className: string };
  'percent-used': string;
  status: React.ReactElement;
  _raw: { budget: number; actual: number; committed: number; remaining: number; percentUsed: number };
  _isLabourGroup?: boolean;
};

// Cost Budget Table View Component
interface CostBudgetTableViewProps {
  categories: CostCategory[];
  resourceBudget?: any;
  totalBudget: number;
  totalActual: number;
  totalCommitted: number;
  totalRemaining: number;
  totalPercentUsed: number;
  totalLabourCost: number;
  getStatusBadge: (percentUsed: number, budget: number, actual: number, committed: number | null) => React.ReactElement;
  calculateRemaining: (budget: number, actual: number, committed: number | null) => { value: number; className: string };
  columns: import('@/components/column-configurator-modal').ColumnDefinition[];
  columnsLoading: boolean;
}

function CostBudgetTableView({
  categories,
  resourceBudget,
  totalBudget,
  totalActual,
  totalCommitted,
  totalRemaining,
  totalPercentUsed,
  totalLabourCost,
  getStatusBadge,
  calculateRemaining,
  columns,
  columnsLoading,
}: CostBudgetTableViewProps) {
  const [columnSorts, setColumnSorts] = useState<ColumnSort[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  // Derive resource budget state
  const hasResourceBudget = Boolean(resourceBudget?.byRole?.length);

  // Rolled-up Labour values from the individual labour categories
  const labourCategories = useMemo(
    () => categories.filter(c => c.type === 'labour'),
    [categories],
  );
  const nonLabourCategories = useMemo(
    () => hasResourceBudget ? categories.filter(c => c.type !== 'labour') : categories,
    [categories, hasResourceBudget],
  );
  const labourBudget  = useMemo(() => labourCategories.reduce((s, c) => s + (c.budget   || 0), 0), [labourCategories]);
  const labourActual  = useMemo(() => labourCategories.reduce((s, c) => s + (c.actual   || 0), 0), [labourCategories]);
  const labourCommitted = useMemo(() => labourCategories.reduce((s, c) => s + (c.committed || 0), 0), [labourCategories]);
  const labourPercentUsed = labourBudget > 0 ? ((labourActual + labourCommitted) / labourBudget) * 100 : 0;
  const labourRemaining = calculateRemaining(labourBudget, labourActual, labourCommitted);

  // Get visible columns in order (safe to compute even while loading)
  const visibleColumns = useMemo(
    () => columns.filter((col) => col.visible).sort((a, b) => a.order - b.order),
    [columns],
  );

  // Transform categories into rows — store raw numeric values for sorting
  // When hasResourceBudget, labour categories are excluded and replaced by the synthetic group row
  const rows = useMemo((): CostBudgetRowData[] => nonLabourCategories.map((category) => {
    const budget = category.budget || 0;
    const actual = category.actual || 0;
    const committed = category.committed || 0;
    const percentUsed = budget > 0 ? ((actual + committed) / budget) * 100 : 0;
    const remaining = calculateRemaining(budget, actual, committed);
    return {
      id: category.id?.toString() || category.name,
      category: category.name,
      categoryCode: category.code,
      budget: formatCurrency(budget),
      actual: formatCurrency(actual),
      committed: committed !== null ? formatCurrency(committed) : '—',
      remaining: { text: formatCurrency(remaining.value), className: remaining.className },
      'percent-used': formatPercent(percentUsed, 1),
      status: getStatusBadge(percentUsed, budget, actual, committed),
      _raw: { budget, actual, committed, remaining: remaining.value, percentUsed },
    };
  }), [nonLabourCategories, getStatusBadge, calculateRemaining]);

  // Synthetic Labour sentinel row — prepended before sorted data rows when hasResourceBudget
  const labourSentinelRow = useMemo((): CostBudgetRowData | null => {
    if (!hasResourceBudget) return null;
    return {
      id: '__labour_group__',
      category: labourCategories.length > 0 ? labourCategories[0].name : 'Labour',
      budget: formatCurrency(labourBudget),
      actual: formatCurrency(labourActual),
      committed: formatCurrency(labourCommitted),
      remaining: { text: formatCurrency(labourRemaining.value), className: labourRemaining.className },
      'percent-used': formatPercent(labourPercentUsed, 1),
      status: getStatusBadge(labourPercentUsed, labourBudget, labourActual, labourCommitted),
      _raw: { budget: labourBudget, actual: labourActual, committed: labourCommitted, remaining: labourRemaining.value, percentUsed: labourPercentUsed },
      _isLabourGroup: true,
    };
  }, [hasResourceBudget, labourCategories, labourBudget, labourActual, labourCommitted, labourRemaining, labourPercentUsed, getStatusBadge]);

  // Sort rows — use _raw numeric values for numeric columns to avoid stringifying JSX/objects
  const sortedRows = useMemo(() => {
    if (columnSorts.length === 0) return rows;
    return [...rows].sort((a, b) => {
      for (const sort of columnSorts) {
        const numericCols = ['budget', 'actual', 'committed', 'remaining', 'percent-used'];
        let cmp = 0;
        if (numericCols.includes(sort.columnId)) {
          const rawKey = sort.columnId === 'remaining' ? 'remaining'
            : sort.columnId === 'percent-used' ? 'percentUsed'
            : sort.columnId as keyof typeof a._raw;
          const aNum = (a._raw as any)[rawKey] as number;
          const bNum = (b._raw as any)[rawKey] as number;
          cmp = aNum - bNum;
        } else {
          const aVal = String((a as any)[sort.columnId] ?? '');
          const bVal = String((b as any)[sort.columnId] ?? '');
          cmp = aVal.localeCompare(bVal);
        }
        if (cmp !== 0) return sort.direction === 'asc' ? cmp : -cmp;
      }
      return 0;
    });
  }, [rows, columnSorts]);

  // Pagination — only counts non-labour rows; sentinel is always prepended outside pagination
  const pagination: PaginationMetadata = useMemo(
    () => calculatePagination(sortedRows.length, currentPage, itemsPerPage),
    [sortedRows.length, currentPage, itemsPerPage],
  );
  const paginatedRows = useMemo(() => {
    const pageRows = paginateArray(sortedRows, currentPage, itemsPerPage).data;
    // Prepend the Labour sentinel row so it always appears at the top, inside TableBody
    return labourSentinelRow ? [labourSentinelRow, ...pageRows] : pageRows;
  }, [sortedRows, currentPage, itemsPerPage, labourSentinelRow]);

  // Build ColumnDef array from visible columns — each column has a typed cell renderer
  // so TanStack drives both header AND cell layout from the same column model
  const columnDefs = useMemo((): ColumnDef<CostBudgetRowData, unknown>[] =>
    visibleColumns.map((col) => ({
      id: col.id,
      accessorKey: col.id,
      header: col.name,
      enableSorting: col.id !== 'status',
      meta: { align: col.align === 'right' || ['budget','actual','committed','remaining','percent-used'].includes(col.id) ? 'right' : 'left' },
      cell: ({ row }: { row: import('@tanstack/react-table').Row<CostBudgetRowData> }) => {
        const r = row.original;
        if (col.id === 'category') {
          return r._isLabourGroup ? (
            <div className="flex items-center gap-2 font-medium">
              <span>{r.category}</span>
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Lock className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-[220px] text-xs">
                    {r.category} costs are driven by the Resource Budget. To change them, revise the Resource Budget.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          ) : r.categoryCode ? (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="font-medium">{r.category}</span>
                </TooltipTrigger>
                <TooltipContent side="right" className="text-xs">
                  <span className="font-mono">{r.categoryCode}</span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <span className="font-medium">{r.category}</span>
          );
        }
        if (col.id === 'remaining') {
          return (
            <span className={cn('font-medium tabular-nums', r.remaining.className)}>
              {r.remaining.text}
            </span>
          );
        }
        if (col.id === 'status') return r.status;
        return <span className="tabular-nums">{(r as any)[col.id] as string}</span>;
      },
    })),
    [visibleColumns, getStatusBadge],
  );

  // Early return AFTER all hooks
  if (columnsLoading) {
    return <p className="text-muted-foreground text-center py-8">Loading...</p>;
  }

  return (
    <DataTableEnhanced
      className="rounded-none"
      columns={columnDefs}
      data={paginatedRows}
      columnSorts={columnSorts}
      onColumnSortChange={(newSorts) => {
        setColumnSorts(newSorts);
        setCurrentPage(1);
      }}
      customRowRenderer={(row, defaultRow) => {
        // Apply row-level styling only — cell content is driven by columnDefs cell renderers
        const r = row.original;
        if (r._isLabourGroup) {
          // Clone the default row with muted background for the Labour group
          return React.cloneElement(
            defaultRow as React.ReactElement<{ className?: string }>,
            { className: 'bg-muted/40' },
          );
        }
        // Normal rows — use default TanStack rendering unchanged
        return defaultRow;
      }}
      footerContent={
        <TableRow className="font-bold bg-muted/50 border-t-2">
          {columnDefs.map((col) => {
            const id = col.id as string;
            return (
              <TableCell
                key={id}
                className={cn(
                  (col.meta as any)?.align === 'right' ? 'text-right' : '',
                  id !== 'category' && id !== 'status' && id !== 'remaining' ? 'tabular-nums' : '',
                )}
              >
                {id === 'category' ? 'Total'
                  : id === 'budget' ? formatCurrency(totalBudget)
                  : id === 'actual' ? formatCurrency(totalActual)
                  : id === 'committed' ? formatCurrency(totalCommitted)
                  : id === 'remaining' ? (
                    <span className={cn('font-bold', totalRemaining >= 0 ? 'text-success-600' : 'text-error-600')}>
                      {formatCurrency(totalRemaining)}
                    </span>
                  ) : id === 'percent-used' ? formatPercent(totalPercentUsed, 1)
                  : id === 'status' ? getStatusBadge(totalPercentUsed, totalBudget, totalActual, totalCommitted)
                  : ''}
              </TableCell>
            );
          })}
        </TableRow>
      }
      paginationContent={
        <PaginationControls
          className="w-full"
          pagination={pagination}
          onPageChange={(p) => setCurrentPage(p)}
          onPageSizeChange={(s) => { setItemsPerPage(s); setCurrentPage(1); }}
        />
      }
    />
  );
}
